import { Category, ColorOption } from '../types/foundObject';
import { isSecretObjectAttributeKey } from './secretObjectAttributes';
import type { ConditionalAttributeLike } from './categoryAttributeFilters';

export type CategoryAttributeDef = ConditionalAttributeLike & {
  secret_detail?: boolean;
  use_as_filter?: boolean;
  sort_order?: number;
};

const LOCATION_ATTRIBUTE_KEYS = new Set([
  'room',
  'zone',
  'saison',
  'etage',
  'storage_box_id',
  'provider_item_id',
  'storageBoxId',
  'providerItemId',
]);

type ColorOptionLike = Partial<ColorOption> & { label?: string };

const isHexColor = (candidate: unknown) =>
  typeof candidate === 'string' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(candidate.trim());

export function normalizeAttributeText(value: string | null | undefined): string {
  if (value == null) return '';
  let v = String(value).trim().toLowerCase();
  if (!v) return '';
  v = v
    .replace(/à|â|ä/g, 'a')
    .replace(/é|è|ê|ë/g, 'e')
    .replace(/î|ï/g, 'i')
    .replace(/ô|ö/g, 'o')
    .replace(/ù|û|ü/g, 'u')
    .replace(/ç/g, 'c');
  return v.replace(/\s+/g, ' ');
}

export function canonicalizeAttributeKey(key: string | null | undefined): string {
  if (key == null) return '';
  let k = normalizeAttributeText(key).replace(/[-\s]+/g, '_');
  switch (k) {
    case 'marque':
    case 'brand':
    case 'fabricant':
      return 'brand';
    case 'modele':
    case 'model':
    case 'phone_model':
      return 'model';
    case 'couleur':
    case 'color':
    case 'colour':
    case 'couleur_coque':
    case 'case_color':
    case 'coque_color':
      return 'color';
    case 'etat':
    case 'state':
    case 'condition':
      return 'condition';
    default:
      return k;
  }
}

export function categoryAttributesRecord(
  category: Pick<Category, 'attributes'> | null | undefined
): Record<string, CategoryAttributeDef> {
  const raw = category?.attributes;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return raw as Record<string, CategoryAttributeDef>;
}

export function sortCategoryDefinitionKeys(
  defs: Record<string, CategoryAttributeDef>
): string[] {
  return Object.keys(defs).sort((a, b) => {
    const orderA = defs[a]?.sort_order ?? Number.MAX_SAFE_INTEGER;
    const orderB = defs[b]?.sort_order ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return a.localeCompare(b);
  });
}

export function filterableAttributeDefs(
  category: Pick<Category, 'attributes'> | null | undefined
): Record<string, CategoryAttributeDef> {
  const defs = categoryAttributesRecord(category);
  return Object.fromEntries(
    sortCategoryDefinitionKeys(defs)
      .map((key) => [key, defs[key]] as [string, CategoryAttributeDef])
      .filter(([, def]) => Boolean(def?.use_as_filter))
  );
}

export function resolveObjectAttributeValue(
  objectAttributes: Record<string, unknown> | undefined,
  categoryKey: string,
  def?: CategoryAttributeDef
): unknown {
  if (!objectAttributes) return undefined;

  const direct = objectAttributes[categoryKey];
  if (direct !== undefined && direct !== null && direct !== '') return direct;

  const label = def?.label?.trim();
  if (label) {
    if (objectAttributes[label] !== undefined && objectAttributes[label] !== null && objectAttributes[label] !== '') {
      return objectAttributes[label];
    }
    const normLabel = normalizeAttributeText(label);
    for (const [k, v] of Object.entries(objectAttributes)) {
      if (v === undefined || v === null || v === '') continue;
      if (normalizeAttributeText(k) === normLabel) return v;
    }
  }

  const canon = canonicalizeAttributeKey(categoryKey);
  for (const [k, v] of Object.entries(objectAttributes)) {
    if (v === undefined || v === null || v === '') continue;
    if (canonicalizeAttributeKey(k) === canon) return v;
  }

  return undefined;
}

function normalizeOptions(options: CategoryAttributeDef['options']): string[] {
  if (!options) return [];
  if (Array.isArray(options)) {
    if (options.length > 0 && typeof options[0] === 'object' && options[0] !== null) {
      return (options as ColorOptionLike[])
        .map((o) => o.name ?? o.label ?? o.value)
        .filter((entry): entry is string => typeof entry === 'string' && entry.trim() !== '');
    }
    return (options as unknown[])
      .filter((entry): entry is string => typeof entry === 'string' && entry.trim() !== '');
  }
  return Object.keys(options);
}

function colorOptionLabel(option: ColorOptionLike): string {
  return option.name ?? option.label ?? option.value ?? '';
}

function colorOptionsMatch(option: ColorOptionLike, strVal: string, rawValue: unknown): boolean {
  const normalizedValue = normalizeAttributeText(strVal);
  if (!normalizedValue) return false;
  const candidates = [option.name, option.label, option.value]
    .filter((entry): entry is string => typeof entry === 'string' && entry.trim() !== '')
    .map((entry) => normalizeAttributeText(entry));
  return candidates.some((candidate) => candidate === normalizedValue) || option.hex === rawValue;
}

function isHiddenOnCard(key: string, def?: CategoryAttributeDef): boolean {
  if (isSecretObjectAttributeKey(key)) return true;
  if (def?.secret_detail) return true;
  if (LOCATION_ATTRIBUTE_KEYS.has(key)) return true;
  return false;
}

export function orderedFilterAttributeKeys(
  attrs: Record<string, unknown>,
  category: Pick<Category, 'attributes'> | null | undefined
): string[] {
  const defs = filterableAttributeDefs(category);
  return sortCategoryDefinitionKeys(defs).filter((k) => {
    const v = resolveObjectAttributeValue(attrs, k, defs[k]);
    return v !== undefined && v !== null && v !== '';
  });
}

export function orderedAttributeKeys(
  attrs: Record<string, unknown>,
  defs: Record<string, CategoryAttributeDef>
): string[] {
  const defKeys = sortCategoryDefinitionKeys(defs).filter((k) => !isHiddenOnCard(k, defs[k]));
  const withValues = defKeys.filter((k) => {
    const v = resolveObjectAttributeValue(attrs, k, defs[k]);
    return v !== undefined && v !== null && v !== '';
  });

  const matchedObjectKeys = new Set<string>();
  for (const dk of withValues) {
    if (attrs[dk] !== undefined && attrs[dk] !== null && attrs[dk] !== '') matchedObjectKeys.add(dk);
    const label = defs[dk]?.label?.trim();
    if (label && attrs[label] !== undefined && attrs[label] !== null && attrs[label] !== '') {
      matchedObjectKeys.add(label);
    }
    const canon = canonicalizeAttributeKey(dk);
    for (const ok of Object.keys(attrs)) {
      if (canonicalizeAttributeKey(ok) === canon) matchedObjectKeys.add(ok);
    }
  }

  const extras = Object.keys(attrs).filter(
    (k) =>
      !matchedObjectKeys.has(k) &&
      !isHiddenOnCard(k) &&
      attrs[k] !== '' &&
      attrs[k] !== null &&
      attrs[k] !== undefined
  );

  return [...withValues, ...extras];
}

export function orderedCardAttributeKeys(
  attrs: Record<string, unknown>,
  category: Pick<Category, 'attributes'> | null | undefined
): string[] {
  const filterKeys = orderedFilterAttributeKeys(attrs, category);
  if (filterKeys.length > 0) return filterKeys;
  return orderedAttributeKeys(attrs, categoryAttributesRecord(category));
}

function isSecretAttributeKey(key: string, def?: CategoryAttributeDef): boolean {
  if (LOCATION_ATTRIBUTE_KEYS.has(key)) return false;
  return def?.secret_detail === true || isSecretObjectAttributeKey(key);
}

export type SecretAttributeEntry = {
  key: string;
  label: string;
  value: string;
};

/** Attributs masqués sur la carte publique (détail secret, indices, etc.). */
export function collectSecretAttributeEntries(
  attrs: Record<string, unknown> | null | undefined,
  category: Pick<Category, 'attributes'> | null | undefined
): SecretAttributeEntry[] {
  const raw = attrs ?? {};
  const defs = categoryAttributesRecord(category);
  const seen = new Set<string>();
  const entries: SecretAttributeEntry[] = [];

  const pushEntry = (key: string, def?: CategoryAttributeDef) => {
    if (seen.has(key) || !isSecretAttributeKey(key, def)) return;
    const value = resolveObjectAttributeValue(raw, key, def);
    const str = value == null ? '' : String(value).trim();
    if (!str) return;
    seen.add(key);
    entries.push({
      key,
      label: def?.label ?? key.replace(/_/g, ' '),
      value: str,
    });
  };

  for (const key of sortCategoryDefinitionKeys(defs)) {
    pushEntry(key, defs[key]);
  }

  for (const key of Object.keys(raw)) {
    pushEntry(key, defs[key]);
  }

  return entries;
}

export function formatAttributeValueForCard(
  value: unknown,
  def: CategoryAttributeDef | undefined
): { text: string; colorHex?: string | null } {
  if (value === null || value === undefined) return { text: '—' };
  if (def?.type === 'boolean') {
    const b = value === true || value === 'true' || value === 1 || value === '1';
    return { text: b ? 'Oui' : 'Non' };
  }
  if (def?.type === 'color_select' && Array.isArray(def.options)) {
    const opts = def.options as ColorOptionLike[];
    const strVal = String(value);
    if (isHexColor(strVal)) {
      const byHex = opts.find((o) => typeof o.hex === 'string' && o.hex.toLowerCase() === strVal.toLowerCase());
      return {
        text: colorOptionLabel(byHex ?? {}) || 'Couleur personnalisée',
        colorHex: strVal,
      };
    }
    const found = opts.find((o) => colorOptionsMatch(o, strVal, value));
    const label = colorOptionLabel(found ?? {});
    return { text: label || strVal, colorHex: found?.hex ?? null };
  }
  if ((def?.type === 'select' || def?.type === 'conditional_select') && def.options) {
    const opts = normalizeOptions(def.options);
    const s = String(value);
    const normalizedValue = normalizeAttributeText(s);
    const match = opts.find((o) => normalizeAttributeText(o) === normalizedValue);
    return { text: match ?? s };
  }

  const asString = String(value);
  if (isHexColor(asString)) {
    return { text: 'Couleur personnalisée', colorHex: asString.trim() };
  }

  return { text: asString };
}
